
import React from 'react';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';

const CompliancePage: React.FC = () => {
  const complianceFAQ = [
    {
      question: "Is AviaGov eAIP fully compliant with ICAO Annex 15?",
      answer: "Yes. AviaGov is architected specifically to meet and exceed ICAO Annex 15 standards for Aeronautical Information Services. It manages the entire AIS data chain from origination to dissemination with full metadata traceability."
    },
    {
      question: "Does the platform support EUROCONTROL Specification 3.0?",
      answer: "Absolutely. We strictly follow the EUROCONTROL Specification for the Electronic Aeronautical Information Publication (eAIP), including XML/XHTML publication structures and the standardized AIRAC lifecycle."
    },
    {
      question: "How does AviaGov ensure Data Quality (ADQ)?",
      answer: "AviaGov enforces ADQ requirements (EU Reg 73/2010) through automated validation rules, electronic data exchange (AIXM 5.1), and an immutable audit trail that tracks every change made to aeronautical features."
    },
    {
      question: "What security certifications does AviaGov hold?",
      answer: "AviaGov's cloud infrastructure is hosted in SOC 2 Type II and ISO 27001 certified data centers. We implement RBAC (Role-Based Access Control) and end-to-end encryption for all mission-critical datasets."
    }
  ];

  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Aviation Compliance Software for ICAO Annex 15 and Annex 19"
        description="Aviation compliance software aligned with ICAO Annex 15, ICAO Annex 19, EUROCONTROL eAIP standards, and aeronautical data quality requirements."
        faq={complianceFAQ}
      />

      <section className="py-24 px-4 max-w-7xl mx-auto">
        <h1 className="text-5xl font-bold font-heading mb-8">Aviation compliance software for ICAO Annex 15 and Annex 19.</h1>
        <p className="text-xl text-slate-400 max-w-3xl mb-16">
          Compliance is not a checkbox; it is the foundation of our platform. We ensure your data meets international standards for safety and reliability.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="p-12 bg-surface border border-white/5 rounded-[2rem]">
            <h3 className="text-2xl font-bold text-white mb-6">Aeronautical Information</h3>
            <ul className="space-y-6">
              <li className="flex items-start">
                 <div className="w-12 h-12 bg-accent/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-6 text-accent font-bold">15</div>
                 <div>
                   <h4 className="text-white font-bold text-lg mb-1">ICAO Annex 15</h4>
                   <p className="text-slate-400 text-sm">Full implementation of Aeronautical Information Services (AIS) standards and recommended practices.</p>
                 </div>
              </li>
              <li className="flex items-start">
                 <div className="w-12 h-12 bg-accent/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-6 text-accent font-bold">3.0</div>
                 <div>
                   <h4 className="text-white font-bold text-lg mb-1">EUROCONTROL Spec 3.0</h4>
                   <p className="text-slate-400 text-sm">Adherence to the latest specification for eAIP, supporting XML/XHTML publication standards.</p>
                 </div>
              </li>
              <li className="flex items-start">
                 <div className="w-12 h-12 bg-accent/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-6 text-accent font-bold">ADQ</div>
                 <div>
                   <h4 className="text-white font-bold text-lg mb-1">Aeronautical Data Quality</h4>
                   <p className="text-slate-400 text-sm">Meeting EU 73/2010 requirements for data integrity, timeliness, and accuracy across the chain.</p>
                 </div>
              </li>
            </ul>
          </div>

          <div className="p-12 bg-surface border border-white/5 rounded-[2rem]">
            <h3 className="text-2xl font-bold text-white mb-6">Safety & Security</h3>
            <ul className="space-y-6">
              <li className="flex items-start">
                 <div className="w-12 h-12 bg-success/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-6 text-success font-bold">19</div>
                 <div>
                   <h4 className="text-white font-bold text-lg mb-1">ICAO Annex 19</h4>
                   <p className="text-slate-400 text-sm">Aligned with State Safety Programme (SSP) requirements for continuous oversight.</p>
                 </div>
              </li>
              <li className="flex items-start">
                 <div className="w-12 h-12 bg-success/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-6 text-success font-bold">27k</div>
                 <div>
                   <h4 className="text-white font-bold text-lg mb-1">ISO/IEC 27001</h4>
                   <p className="text-slate-400 text-sm">Our infrastructure and development processes follow rigorous information security management standards.</p>
                 </div>
              </li>
              <li className="flex items-start">
                 <div className="w-12 h-12 bg-success/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-6 text-success font-bold">SOC</div>
                 <div>
                   <h4 className="text-white font-bold text-lg mb-1">SOC 2 Type II</h4>
                   <p className="text-slate-400 text-sm">Validated controls for security, availability, and processing integrity of client data.</p>
                 </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <FAQSection items={complianceFAQ} />
    </div>
  );
};

export default CompliancePage;
